import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { 
  Shield, Users, FileWarning, Heart, LogOut, TrendingUp,
  Check, X, Eye, Clock
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";

const AdminStoriesPage = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending, approved, all

  const adminToken = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!adminToken) {
      navigate("/admin");
      return;
    }
    fetchStories();
  }, [adminToken, filter]);

  const fetchStories = async () => {
    try {
      let url = `${API}/admin/love-stories`;
      if (filter === "pending") {
        url += "?approved=false";
      } else if (filter === "approved") {
        url += "?approved=true";
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setStories(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
      toast.error("Error fetching stories");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (storyId) => {
    try {
      await axios.post(
        `${API}/admin/love-stories/${storyId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Story approved and published");
      fetchStories();
    } catch (error) {
      toast.error("Error approving story");
    }
  };

  const handleReject = async (storyId) => {
    try {
      await axios.post(
        `${API}/admin/love-stories/${storyId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      toast.success("Story rejected and removed");
      fetchStories();
    } catch (error) {
      toast.error("Error rejecting story");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-800 border-r border-slate-700 p-6 hidden lg:block">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="font-serif text-lg font-bold text-white block">SingleSeen</span>
            <span className="text-rose-400 text-xs">Admin Panel</span>
          </div>
        </div>

        <nav className="space-y-2">
          <Link 
            to="/admin/dashboard"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <TrendingUp className="w-5 h-5" />
            Dashboard
          </Link>
          <Link 
            to="/admin/reports"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <FileWarning className="w-5 h-5" />
            Reports
          </Link>
          <Link 
            to="/admin/users"
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Users className="w-5 h-5" />
            Users
          </Link>
          <Link 
            to="/admin/stories"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-rose-500/10 text-rose-400 font-medium"
          >
            <Heart className="w-5 h-5" />
            Love Stories
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full border-slate-600 text-slate-400 hover:bg-slate-700 hover:text-white"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-white mb-2">Love Stories</h1>
            <p className="text-slate-400">Review and approve user success stories</p>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => setFilter("pending")}
              variant={filter === "pending" ? "default" : "outline"}
              className={filter === "pending" 
                ? "bg-amber-600 text-white" 
                : "border-slate-600 text-slate-400 hover:text-white"
              }
            >
              <Clock className="w-4 h-4 mr-2" />
              Pending
            </Button>
            <Button
              onClick={() => setFilter("approved")}
              variant={filter === "approved" ? "default" : "outline"}
              className={filter === "approved" 
                ? "bg-emerald-600 text-white" 
                : "border-slate-600 text-slate-400 hover:text-white"
              }
            >
              <Check className="w-4 h-4 mr-2" />
              Approved
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-pulse text-rose-500">Loading stories...</div>
          </div>
        ) : stories.length === 0 ? (
          <div className="bg-slate-800 rounded-2xl p-12 border border-slate-700 text-center">
            <Heart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Stories Found</h3>
            <p className="text-slate-400">
              {filter === "pending" ? "No pending stories to review." : "No approved stories yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {stories.map((story) => (
              <div 
                key={story.id}
                className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden"
              >
                {story.photo_url && (
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={story.photo_url.startsWith('http') ? story.photo_url : `${process.env.REACT_APP_BACKEND_URL}${story.photo_url}`}
                      alt={story.couple_names}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-white font-serif text-xl">{story.couple_names}</h3>
                      <p className="text-slate-400 text-sm">
                        {story.location} • {story.married_date}
                      </p>
                    </div>
                    {story.approved ? (
                      <span className="px-3 py-1 rounded-full text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        Approved
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        Pending
                      </span>
                    )}
                  </div>

                  <p className="text-slate-300 text-sm leading-relaxed mb-4 line-clamp-4">
                    {story.story}
                  </p>

                  <p className="text-slate-500 text-xs mb-4">
                    Submitted: {new Date(story.created_at).toLocaleDateString()}
                  </p>

                  {!story.approved && (
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleApprove(story.id)}
                        className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(story.id)}
                        variant="outline"
                        className="flex-1 border-red-600 text-red-400 hover:bg-red-600/10"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminStoriesPage;
